import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout, Typography, Button, Steps, Card, Alert, Space, Tag, Divider, message } from 'antd';
import {
  CalendarOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import DateSelector from './DateSelector';
import SlotList from './SlotList';
import BookingConfirmation from './BookingConfirmation';
import LoadingSpinner from '../common/LoadingSpinner';
import { getAvailableSlots, bookAppointment } from '../../services/slotService';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const cp = searchParams.get('cp');
  const leadid = searchParams.get('leadid');

  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [dates, setDates] = useState([]);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);

  const fetchSlots = useCallback(async () => {
    if (!cp) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAvailableSlots(cp, leadid);
      setSlots(data.slots || []);
      const uniqueDates = [...new Set((data.slots || []).map(s => s.date))].sort();
      setDates(uniqueDates);
      if (uniqueDates.length > 0) setCurrentDateIndex(0);
    } catch (err) {
      setError('Impossible de charger les créneaux disponibles. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, [cp, leadid]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(1);
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    setLoading(true);
    try {
      await bookAppointment({
        date: selectedSlot.date,
        heure: selectedSlot.heure,
        equipe_id: selectedSlot.equipe_id,
        leadid,
        cp,
        surface: null,
        score: selectedSlot.score,
      });
      setConfirmed(true);
      setStep(2);
      message.success('Rendez-vous confirmé !');
    } catch (err) {
      message.error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const currentDateSlots = dates.length > 0
    ? slots.filter(s => s.date === dates[currentDateIndex])
    : [];

  const slotCounts = {};
  dates.forEach(d => {
    slotCounts[d] = slots.filter(s => s.date === d).length;
  });

  const formatDateLabel = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  // Missing params
  if (!cp || !leadid) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#f8faf9' }}>
        <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Alert
            type="error"
            showIcon
            message="Paramètres manquants"
            description="Les paramètres cp et leadid sont requis dans l'URL pour accéder au planning."
          />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8faf9' }}>
      {/* Header */}
      <Header style={{
        background: 'white',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        height: 56,
        lineHeight: '56px',
      }}>
        <Space>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: '#059669',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CalendarOutlined style={{ color: 'white', fontSize: 16 }} />
          </div>
          <Text strong style={{ fontSize: 16 }}>
            EcoRenov<span style={{ color: '#059669' }}>RDV</span>
          </Text>
        </Space>
        <Tag color="green">Espagne</Tag>
      </Header>

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)',
        color: 'white',
        padding: '48px 24px',
        textAlign: 'center',
      }}>
        <Tag style={{
          background: 'rgba(255,255,255,0.15)',
          color: 'white',
          border: 'none',
          marginBottom: 16,
        }}>
          Installation d'Isolation
        </Tag>
        <Title level={2} style={{ color: 'white', marginBottom: 8 }}>
          Planifiez votre rendez-vous
        </Title>
        <Paragraph style={{ color: 'rgba(255,255,255,0.85)', maxWidth: 500, margin: '0 auto' }}>
          Sélectionnez un créneau disponible selon votre secteur.
          Les meilleurs créneaux sont affichés en premier.
        </Paragraph>
      </div>

      {/* Content */}
      <Content style={{ maxWidth: 700, margin: '0 auto', width: '100%', padding: '24px 16px' }}>
        {/* Steps */}
        {!confirmed && (
          <Steps
            current={step}
            size="small"
            style={{ marginBottom: 24 }}
            items={[
              { title: 'Choisir une date' },
              { title: 'Confirmer le créneau' },
              { title: 'Confirmation' },
            ]}
          />
        )}

        {error && (
          <Alert type="error" message={error} showIcon closable
            onClose={() => setError(null)} style={{ marginBottom: 16 }} />
        )}

        {loading && !confirmed ? (
          <LoadingSpinner tip="Recherche des disponibilités..." />
        ) : confirmed ? (
          <BookingConfirmation slot={selectedSlot} onClose={() => window.close()} />
        ) : (
          <>
          {/* Guide rapide */}
          {step === 0 && dates.length > 0 && (
            <Card
              size="small"
              style={{ marginBottom: 16, background: '#f0fdf4', border: '1px solid #bbf7d0' }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Text strong style={{ fontSize: 13, color: '#059669', display: 'block', marginBottom: 8 }}>
                Comment fixer votre rendez-vous ?
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Space size={8}>
                  <CalendarOutlined style={{ color: '#059669' }} />
                  <Text style={{ fontSize: 12 }}>
                    <strong>1.</strong> Choisissez une date ci-dessous — le nombre sur chaque jour indique les créneaux disponibles
                  </Text>
                </Space>
                <Space size={8}>
                  <ClockCircleOutlined style={{ color: '#059669' }} />
                  <Text style={{ fontSize: 12 }}>
                    <strong>2.</strong> Sélectionnez un créneau horaire parmi les équipes proposées
                  </Text>
                </Space>
                <Space size={8}>
                  <CheckCircleOutlined style={{ color: '#059669' }} />
                  <Text style={{ fontSize: 12 }}>
                    <strong>3.</strong> Confirmez votre rendez-vous — c'est tout !
                  </Text>
                </Space>
              </div>
            </Card>
          )}

          <Card styles={{ body: { padding: '16px' } }}>
            <DateSelector
              dates={dates}
              currentIndex={currentDateIndex}
              onChange={setCurrentDateIndex}
              slotCounts={slotCounts}
            />

            <Divider style={{ margin: '8px 0 16px' }} />

            <SlotList
              slots={currentDateSlots}
              selectedSlot={selectedSlot}
              onSelect={handleSlotSelect}
              dateLabel={dates[currentDateIndex] ? formatDateLabel(dates[currentDateIndex]) : null}
            />
          </Card>
          </>
        )}

        {/* Footer action bar */}
        {step === 1 && selectedSlot && !confirmed && !loading && (
          <Card style={{ marginTop: 16, background: '#f8faf9' }} styles={{ body: { padding: '16px' } }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Créneau sélectionné</Text>
                <br />
                <Text strong>
                  {formatDateLabel(selectedSlot.date)} — {selectedSlot.heure}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedSlot.equipe_nom}
                </Text>
              </div>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => { setStep(0); setSelectedSlot(null); }}
                >
                  Modifier
                </Button>
                <Button
                  type="primary"
                  onClick={handleBook}
                  style={{ background: '#059669', borderColor: '#059669' }}
                >
                  Confirmer le RDV
                </Button>
              </Space>
            </div>
          </Card>
        )}
      </Content>

      {/* Footer */}
      <Footer style={{ textAlign: 'center', background: 'white', borderTop: '1px solid #f0f0f0' }}>
        <Text type="secondary">
          {new Date().getFullYear()} EcoRenovRDV — Installation d'Isolation en Espagne
        </Text>
      </Footer>
    </Layout>
  );
};

export default BookingPage;
